import {
  OnFileSaveProps,
  OnFileSaveResult,
  OnImageSaveProps,
} from '@components/form-builder/form-builder';
import { FormV2 } from '@components/form/v2';

import { JoruneyActionFormImageResponse } from 'api/journey-action-forms/requests';
import { request } from 'api/request';

export type SSOPrefillMode = 'inherit' | 'prefill_only' | 'prefill_and_lock' | 'do_not_use';

export type JourneyForm = {
  description: string;
  fieldsConfig: FormV2;
  isPublished: boolean;
  isAutoStart: boolean;
  isPortalForm: boolean;
  journey: string;
  journeyUuid: string;
  name: string;
  slug: string;
  companySlug: string | null;
  shortId: string;
  submissionCount: number;
  submissionMessageConfig: FormV2;
  uuid: string;
  workspace: string;
  portalDescription: string;
  portalImageUrl: string;
  closeAt: string | null;
  responseLimit: number | null;
  allowDuplicateTravellers: boolean;
  closedHeading: string | null;
  closedMessage: string | null;
  ssoPrefillMode: SSOPrefillMode;
};

export type PatchPortalInfoType = Pick<JourneyForm, 'portalDescription'> & {
  name?: string;
  portalImage?: File;
  formName?: string;
  ssoPrefillMode?: SSOPrefillMode;
};

export const loadJourneyForms = async (journeyId: string) => {
  const response = await request.get<JourneyForm[]>(`v1/journey-forms/?journey_id=${journeyId}`);
  return response.data;
};

export const loadTemplateJourneyForms = async (journeyId: string) => {
  const response = await request.get<JourneyForm[]>(
    `v1/templates/journey/${journeyId}/journey-forms/`,
  );
  return response.data;
};

export const loadJourneyForm = async (journeyFormId: string) => {
  const response = await request.get<JourneyForm>(`v1/journey-forms/${journeyFormId}/`);
  return response.data;
};

export const loadTemplateJourneyForm = async (journeyFormId: string) => {
  const response = await request.get<JourneyForm>(`v1/templates/journey-forms/${journeyFormId}/`);
  return response.data;
};

export const deleteJourneyForm = async (journeyFormId: string) => {
  const response = await request.delete<void>(`v1/journey-forms/${journeyFormId}/`);
  return response.data;
};

export const publishJourneyForm = async (journeyFormId: string) => {
  const response = await request.post<void>(`v1/journey-forms/${journeyFormId}/publish`);
  return response.data;
};

export const unpublishJourneyForm = async (journeyFormId: string) => {
  const response = await request.post<void>(`v1/journey-forms/${journeyFormId}/unpublish`);
  return response.data;
};

export const duplicateJourneyForm = async (journeyFormId: string) => {
  const response = await request.post<void>(`v1/journey-forms/${journeyFormId}/duplicate`);
  return response.data;
};

export const createJourneyForm = async (journeyId: string, data: Partial<JourneyForm>) => {
  const response = await request.post<JourneyForm>(`v1/journey-forms/?journey_id=${journeyId}`, {
    ...data,
    journey: journeyId,
  });
  return response.data;
};

export const updateJourneyForm = async (journeyFormId: string, data: Partial<JourneyForm>) => {
  const response = await request.patch<JourneyForm>(`v1/journey-forms/${journeyFormId}/`, data);
  return response.data;
};

export const createFormImage = async ({ data, setProgress }: OnImageSaveProps) => {
  const response = await request.post<JoruneyActionFormImageResponse>(
    `v1/journey-forms/image/`,
    data,
    {
      onUploadProgress: (progressEvent) => {
        if (setProgress) {
          const { loaded, total } = progressEvent;
          if (total) {
            setProgress(loaded / total);
          }
        }
      },
    },
  );
  return response.data;
};

export const createFormFile = async ({ data, setProgress }: OnFileSaveProps) => {
  const response = await request.post<OnFileSaveResult>(`v1/journey-forms/file/`, data, {
    onUploadProgress: (progressEvent) => {
      if (setProgress) {
        const { loaded, total } = progressEvent;
        if (total) {
          setProgress(loaded / total);
        }
      }
    },
  });
  return response.data;
};

type JourneyFormPortalOptions = {
  portals: {
    value: string;
    label: string;
  };
};

export const loadPortalsUsingJourneyForm = async (journeyFormId: string) => {
  const response = await request.get<JourneyFormPortalOptions>(
    `v1/journey-forms/${journeyFormId}/portal-info`,
  );
  return response.data.portals;
};

export const updateJourneyFormPortalInfo = async (
  journeyFormId: string,
  data: PatchPortalInfoType,
) => {
  const response = await request.postForm<JourneyForm>(
    `v1/journey-forms/${journeyFormId}/portal-info`,
    data,
  );
  return response.data;
};
